import { z } from 'zod';
import { apiClient, unwrapApiResponse } from '../shared/api-client';
import { isCurrentContactCreationScope } from '../mobile/navigation/components/vrs/contactCreationRecovery';
import { contactsAPI } from './contactsAPI';
const privateFields = z.object({
    organization: z.string().trim().max(1000).nullable(), notes: z.string().trim().max(1000).nullable(),
    groupIds: z.array(z.string().uuid()).max(50)
});
const contactFields = privateFields.extend({
    name: z.string().trim().min(1, 'Add a contact name.').max(100)
        .refine(value => value.replace(/<[^>]*>/g, '').replace(/&[#\w]+;/g, '').replace(/javascript:/gi, '').replace(/on\w+\s*=/gi, '').trim().length > 0, 'Add a contact name.'),
    phoneNumber: z.string().regex(/^\+?\d{7,16}$/, 'Enter a valid phone number, including its country code.').nullable(),
    email: z.string().email('Enter a valid email address.').max(254).nullable()
});
export function contactEditorDraft(contact) {
    return { name: contact?.name || '', phone: contact?.phone_number || '', email: contact?.email || '',
        organization: contact?.organization || '', notes: contact?.notes || '',
        groupIds: contact?.groups?.map(group => group.id) || contact?.group_ids?.split(',').filter(Boolean) || [] };
}
export function contactEditorInput(draft) {
    const result = contactFields.safeParse({ name: draft.name, phoneNumber: draft.phone.trim().replace(/[\s().-]/g, '') || null,
        email: draft.email.trim() || null, organization: draft.organization.trim() || null,
        notes: draft.notes.trim() || null, groupIds: draft.groupIds });
    if (!result.success)
        throw new Error(result.error.issues[0]?.message || 'Check the contact details.');
    return result.data;
}
/** Saving a directory identity does not edit private fields of an existing saved link. */
export function directoryContactInput(contact) {
    const linkedClientId = contact.linked_client_id;
    if (!linkedClientId || !z.string().uuid().safeParse(linkedClientId).success
        || contact.id !== `platform-${linkedClientId}` || contact.client_id != null) {
        throw new Error('Refresh this directory contact before saving it.');
    }
    const { name, phoneNumber, email } = contactEditorInput({ name: contact.name, phone: contact.phone_number || '', email: contact.email || '',
        organization: '', notes: '', groupIds: [] });
    // Saving the identity does not ask to clear existing private fields or groups.
    return { name, phoneNumber, email, linkedClientId };
}
/** Absolute field updates can be retried; only a checked owned reload closes the editor. */
export async function saveContactEdit(contact, draft, scope) {
    if (!z.string().uuid().safeParse(contact.id).success)
        throw new Error('Save this directory contact before editing it.');
    const requireScope = () => {
        if (!isCurrentContactCreationScope(scope) || contact.client_id !== scope.clientId)
            throw new Error('Your account changed. Reopen Contacts before saving.');
    };
    requireScope();
    // Linked identity is displayed from its account; editing private metadata must not overwrite it.
    const privateInput = privateFields.safeParse({ organization: draft.organization.trim() || null,
        notes: draft.notes.trim() || null, groupIds: draft.groupIds });
    if (!privateInput.success)
        throw new Error(privateInput.error.issues[0]?.message || 'Check the contact details.');
    const payload = contact.linked_client_id ? privateInput.data : contactEditorInput(draft);
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 30000);
    try {
        const response = unwrapApiResponse(await apiClient.put(`/api/contacts/${encodeURIComponent(contact.id)}`, payload, { retryAuth: false, signal: controller.signal }));
        requireScope();
        if (!z.object({ success: z.literal(true) }).safeParse(response).success)
            throw new Error('The update could not be confirmed. Your edits are here; retry to check the save.');
        const detail = await contactsAPI.get(contact.id, controller.signal);
        requireScope();
        return detail;
    }
    finally {
        clearTimeout(timer);
    }
}
